import { Button, Grid, Stack } from '@mui/material'
import { CargoType, VesselType } from '@planning/app/api'
import { VesselVisitCargoFormStore } from '@planning/pages/VesselVisit/Stores/VesselVisitCargoFormStore'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { NumberFieldWithUnit } from '../../atoms/NumberFieldWithUnit'
import { SectionSubtitle } from '../../atoms/SectionSubtitle'

type VesselVisitCargoSubFormProps = {
  store: VesselVisitCargoFormStore
  isCargoTypeLocked?: boolean
  vesselType?: VesselType
}

export const VesselVisitCargoSubForm = observer(
  ({ store, isCargoTypeLocked = false, vesselType }: VesselVisitCargoSubFormProps) => {
    const { t } = useTranslate()

    const cargoTypeLabels: Partial<Record<CargoType, string>> = {
      [CargoType.Container]: t('container', 'Container'),
      [CargoType.GeneralCargo]: t('generalCargo', 'General cargo'),
    }
    const cargoTypeLabel =
      (store.cargoType.value && cargoTypeLabels[store.cargoType.value]) ||
      t('cargoType', 'Cargo type')

    const estimateSubtitle = useMemo(
      () =>
        `${t('discharge', 'Discharge')} / ${t('load', 'Load').toLocaleLowerCase()} / ${t('restow', 'Restow').toLocaleLowerCase()}`,
      [t],
    )

    if (
      vesselType &&
      (vesselType === VesselType.Container || vesselType === VesselType.GeneralCargo)
    )
      store.patchData({ cargoType: vesselType })

    return (
      <Grid container spacing={1}>
        <Grid size={12}>
          <SectionSubtitle label={t('cargoType', 'Cargo type')} />
        </Grid>
        <Grid size={12}>
          {isCargoTypeLocked ? (
            <Button
              variant='contained'
              color='secondary'
              disableElevation
              disableRipple
              sx={{ pointerEvents: 'none' }}
            >
              {cargoTypeLabel}
            </Button>
          ) : (
            <Stack direction='row' spacing={1}>
              <Button
                variant='contained'
                color={
                  isCargoTypeLocked
                    ? 'inherit'
                    : store.cargoType.value === CargoType.Container
                      ? 'secondary'
                      : 'inherit'
                }
                onClick={() => store.patchData({ cargoType: CargoType.Container })}
              >
                {t('container', 'Container')}
              </Button>
              <Button
                variant='contained'
                color={store.cargoType.value === CargoType.GeneralCargo ? 'secondary' : 'inherit'}
                onClick={() => store.patchData({ cargoType: CargoType.GeneralCargo })}
              >
                {t('generalCargo', 'General cargo')}
              </Button>
            </Stack>
          )}
        </Grid>
        <Grid size={12}>
          <SectionSubtitle label={estimateSubtitle} />
        </Grid>
        <Grid container size={12}>
          <Grid size={6}>
            <NumberFieldWithUnit
              id='dischargeEstimate'
              name='dischargeEstimate'
              label={t('discharge', 'Discharge')}
              unit={t('cnt', 'Cnt')}
              fieldStoreProps={store.dischargeEstimate.props}
              restrictionMode='nonNegative'
            />
          </Grid>
          <Grid size={6}>
            <NumberFieldWithUnit
              name='loadEstimate'
              label={`${t('load', 'Load')}`}
              unit={t('cnt', 'Cnt')}
              fieldStoreProps={store.loadEstimate.props}
              restrictionMode='nonNegative'
            />
          </Grid>
          <Grid size={6}>
            <NumberFieldWithUnit
              name='restowEstimate'
              label={`${t('restow', 'Restow')}`}
              unit={t('cnt', 'Cnt')}
              fieldStoreProps={store.restowEstimate.props}
              restrictionMode='nonNegative'
            />
          </Grid>
        </Grid>
      </Grid>
    )
  },
)
